import React, { useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';

// Nivo's default color scheme
const NIVO_COLORS = ['#e8c1a0', '#f47560', '#f1e15b', '#e8a838', '#61cdbb', '#97e3d5'];

const getTooltip = (d) => {
    return (
        <div
			style={{
				background: "white",
				padding: "9px 12px",
				border: "1px solid #ccc",
				borderRadius: "10px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				flexDirection: "column",
				color: "black",
			}}
		>
            {`${d.point.serieId} (${d.point.data.x}): ${d.point.data.y} days`}
        </div>
    )
}

const isEmptyData = (data)=>{
    return data.every(item=>item.data.every(point=>point.y ===0));
}

const ProcessingTimeChart = ({ data, toFilter, setToFilter }) => {
    // Filter data to only show non-hidden series and ensure valid data
    const visibleData = useMemo(() => {
        return data
            .filter(item => !toFilter.includes(item.id))
            .map(item => ({
                ...item,
                data: item.data.filter(point => 
                    point.x != null && 
                    point.y != null && 
                    !isNaN(point.y) && 
                    isFinite(point.y)
                )
            }))
            .filter(item => item.data.length > 0); // Remove series with no valid data
    }, [data, toFilter]);
    
    // Memoize color assignments to keep them consistent
    const colorMap = useMemo(() => {
        const map = {};
        data.forEach((item, index) => {
            map[item.id] = NIVO_COLORS[index % NIVO_COLORS.length];
        });
        return map;
    }, [data]);

    return (
        <>{data.length === 0 || isEmptyData(data) ?
            <div style={{ display: 'flex', justifyContent: "center", alignItems: "center", height: "400px" }}>No records available to display</div> :
            <div style={{ position: 'relative', height: '100%' }}>
                <ResponsiveLine
                    data={visibleData}
                    tooltip={getTooltip}
                    margin={{ top: 50, right: 180, bottom: 50, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        orient: 'bottom',
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Month',
                        legendOffset: 36,
                        legendPosition: 'middle'
                    }}
                    axisLeft={{
                        orient: 'left',
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Processing Time (days)',
                        legendOffset: -40,
                        legendPosition: 'middle'
                    }}
                    colors={(serie) => colorMap[serie.id]}
                    pointSize={10}
                    useMesh={true}
                    animate={false}
                    motionConfig="gentle"
                    enableArea={false}
                    enableSlices={false}
                    isInteractive={true}
                    legends={[]}
                />
                {/* Custom Legend */}
                <div style={{
                    position: 'absolute',
                    right: '20px',
                    top: '50px',
                    bottom: '50px',
                    width: '160px',
                    overflowY: 'auto',
                    fontSize: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '10px',
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                    {data.map((series) => (
                        <div
                            key={series.id}
                            onClick={() => {
                                if (toFilter.includes(series.id)) {
                                    setToFilter(toFilter.filter(el => el !== series.id))
                                } else {
                                    setToFilter([series.id].concat(toFilter))
                                }
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                opacity: toFilter.includes(series.id) ? 0.4 : 1,
                                textDecoration: toFilter.includes(series.id) ? 'line-through' : 'none',
                                color: toFilter.includes(series.id) ? '#999' : '#333',
                                backgroundColor: 'transparent',
                                transition: 'all 0.2s',
                                borderRadius: '3px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <div style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: colorMap[series.id],
                                marginRight: '8px',
                                border: '1px solid rgba(0, 0, 0, 0.3)',
                                flexShrink: 0
                            }} />
                            <span style={{ 
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>{series.id}</span>
                        </div>
                    ))}
                </div>
            </div>
        }</>
    );
}

export default ProcessingTimeChart;
